import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  CenterSpinner,
  DateFilterField,
  EmptyText,
  ExpandableCard,
  FilterField,
  FilterPanel,
  LoadMore,
  M,
  Png,
  RowKV,
  SelectSheet,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import {
  attendanceMeta,
  attendanceStatusText,
  formatCheckInPlace,
  typeLabel,
} from "../../../utils/attendance";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { JOB_TITLE, filterJobPosition, useSupportBusinessEndpoint } from "../../../utils/managerRole";
import {
  ALL_OPTION,
  buildEmployeeSheetData,
  buildOptionSheetData,
  useFilterOptions,
  validId,
} from "../../../utils/useFilterOptions";
import {
  getBusinessAttendanceListApi,
  getBusinessAttendanceStatisticApi,
  getSupportBusinessAttendanceListApi,
  getSupportBusinessAttendanceStatisticApi,
} from "../../../api/attendanceManager.api";

const LIMIT = 10;

// Khớp attendance-management.constant.ts của app.
const EMPLOYEE_TYPE_OPTIONS = [
  { _id: "all", code: "ALL", name: "Tất cả" },
  { _id: "1", code: "KKD", name: "Khối kinh doanh" },
  { _id: "0", code: "KHTKD", name: "Khối hỗ trợ kinh doanh" },
];
const ATTENDANCE_TYPE_OPTIONS = [
  { key: "ALL", label: "Tất cả" },
  { key: "CHECKIN_ON_TIME", label: "Đi làm" },
  { key: "CHECKIN_LATE", label: "Đi muộn" },
  { key: "HALF_SHIFT", label: "Nửa ca" },
  { key: "REGISTER_OUTSIDE", label: "Làm ngoài" },
  { key: "MAKEUP_WORK", label: "Làm bù" },
  { key: "LEAVE", label: "Nghỉ có phép" },
  { key: "ABSENT", label: "Vắng mặt" },
  { key: "HOLIDAY", label: "Cuối tuần / Lễ" },
];
const SORT_BY_OPTIONS = [
  { key: "WORKING_DAY_RATE", label: "Sắp xếp theo số công" },
  { key: "CHECK_IN_TIME", label: "Sắp xếp theo thời gian chấm công" },
  { key: "DATE", label: "Sắp xếp theo ngày" },
];
const SORT_ORDER_OPTIONS = [
  { key: "DESC", label: "Giảm dần" },
  { key: "ASC", label: "Tăng dần" },
];

const DONUT_KEYS = [
  { key: "CHECKIN_ON_TIME", label: "Đi làm", color: "#18C758" },
  { key: "CHECKIN_LATE", label: "Đi muộn", color: "#1277EB" },
  { key: "HALF_SHIFT", label: "Nửa ca", color: "#8B5CF6" },
  { key: "MAKEUP_WORK", label: "Làm bù", color: "#10A7E2" },
  { key: "REGISTER_OUTSIDE", label: "Làm ngoài", color: "#F79D0D" },
  { key: "LEAVE", label: "Nghỉ có phép", color: "#EC5B2A" },
  { key: "HOLIDAY", label: "Cuối tuần / Lễ", color: "#F45757" },
  { key: "ABSENT", label: "Vắng mặt", color: "#E75A90" },
];

const VN_DOW_SHORT = ["CN", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const fmtVNDate = (v) => {
  if (!v) return "-";
  const d = moment(v);
  return `${VN_DOW_SHORT[d.day()]}, ${d.format("DD/MM/YYYY")}`;
};
const timeHHmmss = (v) => (v ? moment(v).format("HH:mm:ss") : "-");
const toDDMMYYYY = (v) => (v ? moment(v).format("DD-MM-YYYY") : undefined);

/* ------------------------------------------------------- donut (SVG) */
const polar = (cx, cy, r, ang) => {
  const rad = ((ang - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};
const arcPath = (cx, cy, r, start, end) => {
  const s = polar(cx, cy, r, end);
  const e = polar(cx, cy, r, start);
  const large = end - start <= 180 ? "0" : "1";
  return ["M", s.x, s.y, "A", r, r, 0, large, 0, e.x, e.y].join(" ");
};
const Donut = ({ items, size = 160, stroke = 30, gap = 2 }) => {
  const r = (size - stroke) / 2;
  const c = size / 2;
  const positive = items.filter((it) => it.value > 0);
  const sum = positive.reduce((s, it) => s + it.value, 0);
  let cursor = -90;
  const arcs = [];
  if (sum > 0) {
    positive.forEach((it) => {
      const sweep = (it.value / sum) * 360;
      const start = cursor + gap / 2;
      const end = Math.max(start, cursor + sweep - gap / 2);
      cursor += sweep;
      arcs.push({ key: it.key, color: it.color, d: arcPath(c, c, r, start, end) });
    });
  }
  return (
    <svg width={size} height={size}>
      <circle cx={c} cy={c} r={r} stroke="#FFFFFF" strokeWidth={stroke} fill="none" />
      {arcs.map((a) => (
        <path key={a.key} d={a.d} stroke={a.color} strokeWidth={stroke} fill="none" />
      ))}
    </svg>
  );
};

/* --------------------------------------------------------------- card */
const AttendanceCard = ({ item, open, onToggle }) => {
  const meta = attendanceMeta(item?.attendanceType);
  const statusText = attendanceStatusText(item?.attendanceType);
  const emp = item?.employeeObject || {};
  const blockName = item?.employee?.saleBlock?.name || emp?.saleBlock?.name || "-";
  const areaName = item?.employee?.saleArea?.name || emp?.saleArea?.name || "-";

  return (
    <ExpandableCard
      open={open}
      onToggle={onToggle}
      header={
        <Flex align="center" gap="12px">
          <Flex w="44px" h="44px" borderRadius="10px" bg={meta.bg} align="center" justify="center" flexShrink={0}>
            <Png src={appImages.icon_check_in} size={24} color={meta.color} />
          </Flex>
          <Box flex="1" minW="0">
            <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={1}>
              {emp?.fullName?.trim() || "-"} - {emp?.code?.trim() || "-"}
            </Text>
            <Box h="4px" />
            {/* Trạng thái + ngày nằm trên 1 dòng, không xuống dòng (khớp app) */}
            <Flex align="center" gap="8px" whiteSpace="nowrap">
              {statusText === "Đã chấm công" ? <Png src={appImages.icon_check_circle} size={16} color={meta.color} /> : null}
              <Text fontSize="13px" fontWeight={500} color={meta.color}>
                {statusText}
              </Text>
              <Text fontSize="13px" fontWeight={500} color={M.tertiary}>
                {fmtVNDate(item?.date)}
              </Text>
            </Flex>
          </Box>
        </Flex>
      }
    >
      <RowKV
        label="Phân loại"
        value={
          item?.attendanceType === "HOLIDAY"
            ? item?.holiday?.name || "Ngày nghỉ cuối tuần"
            : typeLabel(item?.attendanceType)
        }
        labelFlex="1"
      />
      <RowKV
        label="Số công"
        value={typeof item?.workingDayRate === "number" ? String(item.workingDayRate) : "0"}
        labelFlex="1"
      />
      <RowKV label="Thời gian chấm công" value={timeHHmmss(item?.checkinTime)} labelFlex="1" />
      <RowKV label="Khối" value={blockName} labelFlex="1" />
      <RowKV label="Vùng" value={areaName} labelFlex="1" />
      <RowKV label="Địa điểm chấm công" value={formatCheckInPlace(item) || "-"} labelFlex="1" />
      <RowKV label="Ghi chú" value={item?.note || "-"} labelFlex="1" />
    </ExpandableCard>
  );
};

/* --------------------------------------------------------------- page */
const AttendanceEmployeeList = () => {
  const isDesktop = useIsDesktop();
  const { user } = useEmployeeAuth();
  const supportEndpoint = useSupportBusinessEndpoint(user);
  const listApi = supportEndpoint ? getSupportBusinessAttendanceListApi : getBusinessAttendanceListApi;
  const statisticApi = supportEndpoint
    ? getSupportBusinessAttendanceStatisticApi
    : getBusinessAttendanceStatisticApi;
  const jobPosition = filterJobPosition(user);
  const employeeSheetKey = supportEndpoint ? "EMPLOYEE_SUPPORT" : "EMPLOYEE";

  const [filterOpen, setFilterOpen] = useState(true);
  const [employeeType, setEmployeeType] = useState(EMPLOYEE_TYPE_OPTIONS[0]);
  const [attendanceType, setAttendanceType] = useState("ALL");
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [supportDept, setSupportDept] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [employee, setEmployee] = useState(ALL_OPTION);
  const [fromDate, setFromDate] = useState(moment().subtract(1, "month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [sortBy, setSortBy] = useState("WORKING_DAY_RATE");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(() => {
    if (sheet.type === "BLOCK") return { saleAreaId: validId(area) };
    if (sheet.type === "EMPLOYEE" || sheet.type === "EMPLOYEE_SUPPORT")
      return { saleBlockId: validId(block), saleAreaId: validId(area) };
    return undefined;
  }, [sheet.type, area, block]);
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const buildParams = useCallback(
    (p) => {
      const q = {
        page: p,
        limit: LIMIT,
        type: attendanceType,
        sortBy,
        sortOrder,
      };
      if (fromDate) q.fromDate = toDDMMYYYY(fromDate);
      if (toDate) q.toDate = toDDMMYYYY(toDate);
      if (validId(area)) q.saleAreaId = validId(area);
      if (validId(block)) q.saleBlockId = validId(block);
      if (validId(supportDept)) q.supportDepartmentId = validId(supportDept);
      if (validId(branch)) q.branchId = validId(branch);
      if (employeeType?._id === "0" || employeeType?._id === "1") q.department = Number(employeeType._id);
      if (validId(employee)) q.employeeId = validId(employee);
      return q;
    },
    [attendanceType, sortBy, sortOrder, fromDate, toDate, area, block, supportDept, branch, employeeType, employee]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const params = buildParams(p);
        // API thống kê KHÔNG nhận page/limit/sortBy/sortOrder (khớp FetchStatsPayload của app).
        const statParams = { ...params };
        delete statParams.page;
        delete statParams.limit;
        delete statParams.sortBy;
        delete statParams.sortOrder;
        const [data, statistic] = await Promise.all([
          listApi(params),
          p === 1 ? statisticApi(statParams).catch(() => null) : Promise.resolve(null),
        ]);
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        if (statistic) setStats(statistic);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [listApi, statisticApi, buildParams]
  );

  useEffect(() => {
    setExpanded({});
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attendanceType,
    sortBy,
    sortOrder,
    fromDate,
    toDate,
    area?._id,
    block?._id,
    supportDept?._id,
    branch?._id,
    employeeType?._id,
    employee?._id,
  ]);

  const donutItems = useMemo(() => {
    // BE có thể trả { legendCounts } hoặc bọc thêm 1 lớp; chấp nhận cả 2 dạng.
    const lc = stats?.legendCounts || stats?.data?.legendCounts || stats || {};
    return DONUT_KEYS.map((k) => ({ ...k, value: Number(lc?.[k.key] ?? 0) }));
  }, [stats]);
  const donutSum = donutItems.reduce((s, it) => s + it.value, 0);

  const attTypeLabel = ATTENDANCE_TYPE_OPTIONS.find((x) => x.key === attendanceType)?.label ?? "Tất cả";
  const sortByLabel = SORT_BY_OPTIONS.find((x) => x.key === sortBy)?.label ?? "Sắp xếp";
  const sortOrderLabel = SORT_ORDER_OPTIONS.find((x) => x.key === sortOrder)?.label ?? "Giảm dần";
  const employeeLabel = employee?._id !== "all" ? `${employee?.fullName || "-"} (${employee?.code || "-"})` : "Tất cả";

  // Lưới bộ lọc theo chức vụ - khớp renderFilterGrid của app.
  const filterFields = useMemo(() => {
    const f = {
      empType: (
        <FilterField
          key="empType"
          label="Loại nhân sự"
          value={employeeType?.name || "Tất cả"}
          onClick={() => sheet.open("EMP_TYPE")}
        />
      ),
      attType: (
        <FilterField key="attType" label="Phân loại công" value={attTypeLabel} onClick={() => sheet.open("ATT_TYPE")} />
      ),
      sdept: (
        <FilterField
          key="sdept"
          label="Phòng"
          value={supportDept?.name || "Tất cả"}
          onClick={() => sheet.open("SUPPORT_DEPARTMENT")}
        />
      ),
      branch: (
        <FilterField key="branch" label="Chi nhánh" value={branch?.name || "Tất cả"} onClick={() => sheet.open("BRANCH")} />
      ),
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      employee: (
        <FilterField key="employee" label="Nhân sự" value={employeeLabel} onClick={() => sheet.open(employeeSheetKey)} />
      ),
      from: <DateFilterField key="from" label="Từ ngày" value={fromDate} onChange={setFromDate} />,
      to: <DateFilterField key="to" label="Đến ngày" value={toDate} onChange={setToDate} />,
    };
    switch (jobPosition) {
      case JOB_TITLE.NHAN_VIEN:
        return [f.from, f.to];
      case JOB_TITLE.TRUONG_KHOI:
        return [f.attType, f.block, f.employee, f.from, f.to];
      case JOB_TITLE.TRUONG_VUNG:
        return [f.attType, f.area, f.block, f.employee, f.from, f.to];
      case JOB_TITLE.TRUONG_PHONG:
        return [f.attType, f.sdept, f.employee, f.from, f.to];
      default:
        return [f.empType, f.attType, f.sdept, f.branch, f.area, f.block, f.employee, f.from, f.to];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, employeeType, attTypeLabel, supportDept, branch, area, block, employeeLabel, fromDate, toDate]);

  const sheetTitle =
    {
      EMP_TYPE: "Loại nhân sự",
      ATT_TYPE: "Phân loại công",
      SUPPORT_DEPARTMENT: "Phòng",
      BRANCH: "Chi nhánh",
      AREA: "Vùng",
      BLOCK: "Khối",
      EMPLOYEE: "Nhân sự",
      EMPLOYEE_SUPPORT: "Nhân sự",
      SORT_BY: "Sắp xếp theo",
      SORT_ORDER: "Thứ tự",
    }[sheet.type] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "EMP_TYPE") return EMPLOYEE_TYPE_OPTIONS.map((x) => ({ key: x._id, label: x.name, raw: x }));
    if (sheet.type === "ATT_TYPE") return ATTENDANCE_TYPE_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT_BY") return SORT_BY_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT_ORDER") return SORT_ORDER_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SUPPORT_DEPARTMENT") return buildOptionSheetData(options.items, supportDept);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "EMPLOYEE" || sheet.type === "EMPLOYEE_SUPPORT")
      return buildEmployeeSheetData(options.items, employee);
    return [];
  }, [sheet.type, options.items, supportDept, branch, area, block, employee]);

  const selectedKey = {
    EMP_TYPE: employeeType?._id,
    ATT_TYPE: attendanceType,
    SUPPORT_DEPARTMENT: supportDept?._id,
    BRANCH: branch?._id,
    AREA: area?._id,
    BLOCK: block?._id,
    EMPLOYEE: employee?._id,
    EMPLOYEE_SUPPORT: employee?._id,
    SORT_BY: sortBy,
    SORT_ORDER: sortOrder,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "EMP_TYPE") setEmployeeType(raw);
    if (sheet.type === "ATT_TYPE") setAttendanceType(raw);
    if (sheet.type === "SUPPORT_DEPARTMENT") setSupportDept(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "EMPLOYEE" || sheet.type === "EMPLOYEE_SUPPORT") setEmployee(raw);
    if (sheet.type === "SORT_BY") setSortBy(raw);
    if (sheet.type === "SORT_ORDER") setSortOrder(raw);
    sheet.close();
    setSheetSearch("");
  };

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={["SUPPORT_DEPARTMENT", "BRANCH", "AREA", "BLOCK", "EMPLOYEE", "EMPLOYEE_SUPPORT"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => {
      const meta = attendanceMeta(it?.attendanceType);
      const emp = it?.employeeObject || {};
      return {
        id: it._id,
        raw: it,
        meta,
        employee: `${emp?.fullName?.trim() || "-"} - ${emp?.code?.trim() || "-"}`,
        status: attendanceStatusText(it?.attendanceType),
        date: fmtVNDate(it?.date),
        type:
          it?.attendanceType === "HOLIDAY"
            ? it?.holiday?.name || "Ngày nghỉ cuối tuần"
            : typeLabel(it?.attendanceType),
        rate: typeof it?.workingDayRate === "number" ? String(it.workingDayRate) : "0",
        time: timeHHmmss(it?.checkinTime),
        block: it?.employee?.saleBlock?.name || emp?.saleBlock?.name || "-",
        area: it?.employee?.saleArea?.name || emp?.saleArea?.name || "-",
        place: formatCheckInPlace(it) || "-",
        note: it?.note || "-",
      };
    });

    return (
      <DesktopPage title="Quản lý chuyên cần">
        <DesktopPanel>
          <Flex align="center" gap="32px" flexWrap="wrap">
            <Flex minW="200px" justify="center">
              {donutSum > 0 ? (
                <Donut items={donutItems} />
              ) : (
                <Text fontSize="13px" color="#E5484D" textAlign="center">
                  Chưa có bản ghi nào
                </Text>
              )}
            </Flex>
            <Box
              flex="1"
              minW="360px"
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
              gap="10px"
            >
              {donutItems.map((it) => {
                const pct = donutSum > 0 ? Math.round((it.value / donutSum) * 100) : 0;
                return (
                  <Flex key={it.key} align="center" gap="8px">
                    <Box
                      w="16px"
                      h="16px"
                      borderRadius="5px"
                      bg={it.color}
                      border="1px solid"
                      borderColor="rgba(8,20,45,0.06)"
                      flexShrink={0}
                    />
                    <Text fontSize="13px" fontWeight={500} color={M.baseContent} whiteSpace="nowrap">
                      {it.label}:
                    </Text>
                    <Text fontSize="13px" fontWeight={500} color={M.tertiary} whiteSpace="nowrap">
                      {it.value.toLocaleString("vi-VN")}-{pct}%
                    </Text>
                  </Flex>
                );
              })}
            </Box>
          </Flex>
        </DesktopPanel>

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "200px" }),
            )}
            <Box w="200px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp theo
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT_BY")}
              >
                {sortByLabel}
              </Box>
            </Box>
            <Box w="160px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Thứ tự
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT_ORDER")}
              >
                {sortOrderLabel}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            rows={rows}
            headers={[
              { key: "employee", label: "Nhân viên", align: "left", width: "18%" },
              { key: "date", label: "Ngày", width: "9%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "11%",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={row.meta.color}>
                    {row.status}
                  </Text>
                ),
              },
              { key: "type", label: "Phân loại", width: "11%" },
              { key: "rate", label: "Số công", width: "7%" },
              { key: "time", label: "Giờ chấm công", width: "10%" },
              { key: "block", label: "Khối", align: "left", width: "10%" },
              { key: "area", label: "Vùng", align: "left", width: "8%" },
              { key: "place", label: "Địa điểm", align: "left", width: "10%" },
              { key: "note", label: "Ghi chú", align: "left", width: "6%" },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Quản lý chuyên cần" />

      <Box px="16px" pt="12px">
        {/* Biểu đồ tròn: donut bên trái, chú thích 1 cột bên phải - khớp
            DonutAttendanceManagement (legendColumns=1) của app. */}
        <Box bg="white" borderRadius="8px" border="1px solid" borderColor={M.border} p="12px">
          <Flex align="center" justify="space-between" mt="8px" gap="8px">
            <Flex w="50%" justify="center">
              {donutSum > 0 ? (
                <Donut items={donutItems} />
              ) : (
                <Text fontSize="12px" color="#E5484D" textAlign="center">
                  Chưa có bản ghi nào
                </Text>
              )}
            </Flex>
            <Flex flex="1" direction="column" justify="center" gap="8px">
              {donutItems.map((it) => {
                const pct = donutSum > 0 ? Math.round((it.value / donutSum) * 100) : 0;
                return (
                  <Flex key={it.key} align="center" gap="8px">
                    <Box
                      w="16px"
                      h="16px"
                      borderRadius="5px"
                      bg={it.color}
                      border="1px solid"
                      borderColor="rgba(8,20,45,0.06)"
                      flexShrink={0}
                    />
                    <Text fontSize="10px" fontWeight={500} color={M.baseContent} whiteSpace="nowrap">
                      {it.label}:
                    </Text>
                    <Text fontSize="10px" fontWeight={500} color={M.tertiary} whiteSpace="nowrap">
                      {it.value.toLocaleString("vi-VN")}-{pct}%
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Box>

        <Box h="12px" />

        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        {/* Hai nút sắp xếp - khớp app */}
        <Flex align="center" justify="space-between" pt="12px" gap="12px">
          <Flex
            as="button"
            type="button"
            flex="1"
            minW="0"
            align="center"
            justify="space-between"
            h="40px"
            px="10px"
            bg="white"
            borderRadius="8px"
            onClick={() => sheet.open("SORT_BY")}
          >
            <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={1}>
              {sortByLabel}
            </Text>
            <Png src={appImages.icon_chevron_down} size={18} />
          </Flex>
          <Flex
            as="button"
            type="button"
            align="center"
            justify="space-between"
            gap="6px"
            h="40px"
            px="10px"
            bg="white"
            borderRadius="8px"
            flexShrink={0}
            onClick={() => sheet.open("SORT_ORDER")}
          >
            <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
              {sortOrderLabel}
            </Text>
            <Png src={appImages.icon_chevron_down} size={18} />
          </Flex>
        </Flex>

        <Box h="12px" />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <AttendanceCard
                key={it._id}
                item={it}
                open={!!expanded[it._id]}
                onToggle={() => setExpanded((p) => ({ ...p, [it._id]: !p[it._id] }))}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default AttendanceEmployeeList;
